import { useEffect, useRef } from "react"
import type { PointerEvent as ReactPointerEvent } from "react"
import { ChevronUp, ChevronDown, X } from "lucide-react"
import { useStudioStore } from "../../store/studio-store"
import { DevToolsPanel } from "../devtools-panel"
import { useUIBreakpoint } from "@/hooks/use-ui-breakpoint"
import { ResponsivePanel } from "../responsive-panel"

/**
 * Bottom dock for DevTools (Sub-project B PR5).
 *
 * Closed state: thin h-6 handle bar with chevron + label, click to open.
 * Open state: panel with header (chevron, label, close X) + draggable top resize handle + DevToolsPanel content.
 *
 * Persistence + state in ide-v2-slice (devtoolsDockOpen / devtoolsDockHeight).
 * Esc closes when open. Pointer-drag on top edge resizes (clamped to [80, 600] in setter).
 */
export function DevToolsDock() {
  const isOpen = useStudioStore((s) => s.devtoolsDockOpen)
  const height = useStudioStore((s) => s.devtoolsDockHeight)
  const studioPersona = useStudioStore((s) => s.studioPersona)
  const toggle = useStudioStore((s) => s.toggleDevtoolsDock)
  const setHeight = useStudioStore((s) => s.setDevtoolsDockHeight)
  const isDeveloper = studioPersona === "developer"

  // Phase 531 (SPEC §2.2 / RESP-03): at md/lg the dock folds into a bottom Sheet
  // (md = modal, lg = slide-over) via ResponsivePanel; at xl+ it stays the inline
  // bottom-docked resizable panel. Reuses the existing devtoolsDock* state.
  const { bp } = useUIBreakpoint()
  const isFolded = bp === "md" || bp === "lg"

  const dragRef = useRef<{ startY: number; origH: number } | null>(null)

  // Esc closes only while open AND inline (xl+). At md/lg the Sheet owns ESC, so
  // gating here avoids a double-toggle (Sheet onOpenChange + this handler).
  useEffect(() => {
    if (!isDeveloper || !isOpen || isFolded) return
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") toggle()
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [isDeveloper, isOpen, isFolded, toggle])

  function onPointerDown(e: ReactPointerEvent<HTMLDivElement>) {
    dragRef.current = { startY: e.clientY, origH: height }
    e.currentTarget.setPointerCapture(e.pointerId)
  }
  function onPointerMove(e: ReactPointerEvent<HTMLDivElement>) {
    if (!dragRef.current) return
    const dy = dragRef.current.startY - e.clientY  // drag up → larger
    setHeight(dragRef.current.origH + dy)
  }
  function onPointerUp(e: ReactPointerEvent<HTMLDivElement>) {
    dragRef.current = null
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId)
    }
  }

  if (!isDeveloper) return null

  if (!isOpen) {
    return (
      <div
        data-testid="devtools-handle"
        onClick={toggle}
        className="h-6 border-t border-border bg-muted flex items-center justify-center text-[10px] text-muted-foreground hover:bg-accent cursor-pointer select-none"
      >
        <ChevronUp size={12} strokeWidth={1.7} className="mr-1" />
        DevTools
      </div>
    )
  }

  // Shared header + content (no outer wrapper / resize handle — those differ per mode).
  const dockBody = (
    <>
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-1 border-b border-border bg-muted">
        <div className="flex items-center gap-1 text-xs font-semibold text-foreground">
          <ChevronDown size={12} strokeWidth={1.7} />
          DevTools
        </div>
        <button
          type="button"
          aria-label="Close DevTools"
          onClick={toggle}
          className="text-muted-foreground hover:text-foreground"
        >
          <X size={14} strokeWidth={1.7} />
        </button>
      </div>
      {/* Content */}
      <div className="flex-1 min-h-0 overflow-auto">
        <DevToolsPanel />
      </div>
    </>
  )

  // md / lg — fold into a bottom Sheet (modal at md, slide-over at lg). The Sheet
  // (Radix Dialog) provides its own ESC + overlay close → onOpenChange → toggle.
  if (isFolded) {
    return (
      <ResponsivePanel
        id="devtools"
        side="bottom"
        inlineFrom="xl"
        modalFrom="md"
        open={isOpen}
        onOpenChange={() => toggle()}
        size={height}
        onResize={setHeight}
      >
        <div className="flex h-full flex-col bg-card">{dockBody}</div>
      </ResponsivePanel>
    )
  }

  // xl+ — existing inline bottom-docked resizable panel (unchanged).
  return (
    <div
      data-testid="devtools-dock-panel"
      className="border-t border-border bg-card flex flex-col"
      style={{ height: `${height}px` }}
    >
      {/* Resize handle (top edge) */}
      <div
        className="h-1 cursor-ns-resize bg-border hover:bg-foreground/30"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      />
      {dockBody}
    </div>
  )
}
